import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ROUTES_CONFIG } from "@/config/routes.config"
import { useTRPC } from "@/trpc"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { Navigate, useNavigate } from "react-router-dom"
import { useTypedSearchParams } from "react-router-typesafe-routes"
import { z } from "zod"
import * as Icons from "lucide-react"
import { useEffect } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { toast } from "sonner"

const registerSchema = z.object({
  email: z.string().email(),
  password: z.string().min(4),
  invite_token: z.string(),
  first_name: z.string(),
  last_name: z.string(),
})

type RegisterForm = z.infer<typeof registerSchema>

const RegisterPage = () => {
  const navigate = useNavigate();
  const { trpc } = useTRPC()
  const [{ token }] = useTypedSearchParams(ROUTES_CONFIG.AUTH.REGISTER)
  const form = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const tokenQuery = useQuery({
    queryKey: ['token', token],
    queryFn: async () => {
      const validation = await trpc.auth.verifyToken.query({ token })
      return validation
    }
  })

  useEffect(()=>{
    if(!tokenQuery.data) return;

    form.setValue("invite_token", tokenQuery.data.token)
    form.setValue("email", tokenQuery.data.email)
  }, [tokenQuery.data])

  const registerMutation = useMutation({
    mutationFn: async (data:RegisterForm) => {
      await trpc.auth.register.mutate({
        email: data.email,
        password: data.password,
        invite_token: data.invite_token,
        first_name: data.first_name,
        last_name: data.last_name
      })
    },
    onSuccess: () => {
      toast.success("Account succesvol aangemaakt")
      navigate(ROUTES_CONFIG.AUTH.LOGIN.$buildPath({}))
    }
  })

  const handleSubmit = () => {
    registerMutation.mutate(form.getValues())
  }

  if(tokenQuery.isLoading) {
    return <p>Loading...</p>
  }

  if(!tokenQuery.data) {
    return <Navigate to={ROUTES_CONFIG.AUTH.LOGIN.$buildPath({})} replace/>
  }

  return (
    <div className="flex items-center justify-center flex-col h-dvh w-dvw">
      <img
        src="/img/bg/gradient-green-black.png"
        className="absolute top-0 left-0 w-full h-full object-cover -z-20"
        alt=""
      />
      <img
        src="/img/logo/letters-center.svg"
        className="absolute top-4 left-1/2 -translate-x-1/2 h-10 -z-10"
        alt=""
      />

      <div className="max-w-md w-full space-y-8 flex flex-col bg-white/10 p-6 rounded-xl border shadow-sm">
        <div className="space-y-2">
          <h1 className="text-xl font-medium text-center">
            Registreren in Scoutdeck
          </h1>
          <p className="text-center text-sm">
            Vul jouw wachtwoord in om een account te maken
          </p>
        </div>

        <div className="space-y-2">
          <Input
            disabled
            {...form.register("email")}
            placeholder="Email"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />

          <Input
            autoFocus
            {...form.register("first_name")}
            placeholder="Voornaam"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />
          <Input
            {...form.register("last_name")}
            placeholder="Familienaam"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />

          <Input
            {...form.register("password")}
            type="password"
            placeholder="Wachtwoord"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />
        </div>

        <Button onClick={() => handleSubmit()} variant="bright" shape="pill" disabled={!form.formState.isValid || registerMutation.isLoading}>
          {registerMutation.isLoading && <Icons.Loader2 className="w-4 h-4 animate-spin" />}
          Registreren
        </Button>
      </div>
    </div>
  )
}

export default RegisterPage